/**
 * The builder page. Composes Canvas + Inspector + TopBar around a
 * `BuilderProvider`. Owns global keyboard shortcuts.
 *
 * There is no left-hand library; adding steps is done entirely through the
 * NodePicker popup, triggered from:
 *   - the empty-state "Add a step" button,
 *   - the floating "+" on the canvas,
 *   - the "+" affordance on each node and edge midpoint.
 */

import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { BuilderProvider, useBuilder } from './state/context';
import { Canvas } from './canvas/canvas';
import { Inspector } from './inspector/inspector';
import { TopBar } from './top-bar';
import { createEmptyState } from './state/reducer';
import { loadDraft, saveDraft } from './state/persistence';
import { validate } from './state/validation';
import type { DefinitionState } from './types';

export function WorkflowBuilderPage() {
  const { draftId } = useParams();
  const navigate = useNavigate();

  const [initialState, setInitialState] = React.useState<DefinitionState | null>(null);

  React.useEffect(() => {
    if (!draftId) return;
    const existing = loadDraft(draftId);
    if (existing) {
      setInitialState(existing);
    } else {
      const fresh = { ...createEmptyState(), id: draftId };
      saveDraft(fresh);
      setInitialState(fresh);
    }
  }, [draftId]);

  if (!draftId) {
    navigate('/app/workflows', { replace: true });
    return null;
  }

  if (!initialState) {
    return <LoadingPage />;
  }

  return (
    <BuilderProvider initialState={initialState}>
      <BuilderShell />
    </BuilderProvider>
  );
}

function LoadingPage() {
  return (
    <div className="flex h-[calc(100vh-0px)] items-center justify-center text-sm text-neutral-500 dark:text-neutral-400">
      Loading draft…
    </div>
  );
}

function BuilderShell() {
  const { state, dispatch, setSelectedNodeId, selectedNodeId, undo, redo } = useBuilder();
  const issues = React.useMemo(() => validate(state), [state]);

  React.useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const mod = e.metaKey || e.ctrlKey;
      if (mod && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        undo();
      } else if (mod && (e.key === 'Z' || (e.shiftKey && e.key === 'z') || e.key === 'y')) {
        e.preventDefault();
        redo();
      } else if (mod && e.key === 's') {
        e.preventDefault();
        saveDraft(state);
      } else if (!mod && (e.key === 'Delete' || e.key === 'Backspace')) {
        const target = e.target as HTMLElement | null;
        if (
          target &&
          (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)
        )
          return;
        if (selectedNodeId) {
          e.preventDefault();
          dispatch({ type: 'remove-node', nodeId: selectedNodeId });
          setSelectedNodeId(null);
        }
      } else if (e.key === 'Escape') {
        setSelectedNodeId(null);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [undo, redo, selectedNodeId, dispatch, setSelectedNodeId, state]);

  return (
    <div className="flex h-[calc(100vh-0px)] flex-col bg-neutral-50 dark:bg-neutral-900 -m-8 sm:-m-12 relative">
      <TopBar issues={issues} />
      <div className="flex flex-1 min-h-0">
        <main className="relative flex-1 min-w-0">
          <Canvas />
        </main>
        <Inspector issues={issues} />
      </div>
    </div>
  );
}
